// ItemType-verdier og metadata
export const ITEM_TYPE_VALUES = [0, 1, 2, 3, 4, 5, 6] as const;
export type ItemTypeValue = (typeof ITEM_TYPE_VALUES)[number];

export interface ItemTypeOption {
  value: ItemTypeValue;
  labelNb: string;
  labelEn: string;
  description: string;
  isTracked: boolean; // true for 4/5/6 (lagerstyrt)
}

export const ITEM_TYPES: ItemTypeOption[] = [
  { value: 0, labelNb: 'Standard', labelEn: 'Default', description: 'Standard vare uten spesialhåndtering', isTracked: false },
  { value: 1, labelNb: 'Batchnummer', labelEn: 'Batch number', description: 'Batch kun som dokumentasjon på ordre', isTracked: false },
  { value: 2, labelNb: 'Serienummer', labelEn: 'Serial number', description: 'Serienummer kun som dokumentasjon på ordre', isTracked: false },
  { value: 3, labelNb: 'STAND 005', labelEn: 'Stand 005', description: 'Spesiell dokumentasjonstype', isTracked: false },
  { value: 4, labelNb: 'Batchnummer m/holdbarhet', labelEn: 'Batch with expiry date', description: 'Batchnummer + utløpsdato med full lagerstyring', isTracked: true },
  { value: 5, labelNb: 'Batchnummer lagerstyrt', labelEn: 'Batch with inventory tracking', description: 'Batchnummer med full lagerstyring', isTracked: true },
  { value: 6, labelNb: 'Serienummer lagerstyrt', labelEn: 'Serial with inventory tracking', description: 'Individuelt serienummer per enhet med full lagerstyring', isTracked: true },
];

export function getItemTypeLabel(value: number | null | undefined, lang: 'nb' | 'en' = 'nb'): string {
  if (value === null || value === undefined) return '-';
  const opt = ITEM_TYPES.find(t => t.value === value);
  if (!opt) return String(value);
  return lang === 'nb' ? opt.labelNb : opt.labelEn;
}

export enum ItemTypeTransitionClass {
  Trivial = 1,
  Downgrade = 2,
  UpgradeWithStock = 3,
}

export enum ItemTypeChangeStrategy {
  Direct = 0,
  Aggregate = 1,
  Placeholder = 2,
  Manual = 3,
}

export interface ItemTypeChangePreviewBinRow {
  itemBinsId: number;
  warehouseID: string;
  binCode: string;
  quantity: number;
  currentSerialNo?: string | null;
  currentBatchNo?: string | null;
  currentExpiryDate?: string | null;
}

export interface ItemTypeChangePreview {
  companyNo: string;
  itemNo: string;
  description?: string | null;
  currentItemType: number;
  newItemType: number;
  transitionClass: ItemTypeTransitionClass;
  defaultStrategy: ItemTypeChangeStrategy;
  availableStrategies: ItemTypeChangeStrategy[];
  requiresUserDecision: boolean;
  itemBinsCount: number;
  totalQuantity: number;
  affectedLocations: number;
  existingSerialsCount: number;
  existingBatchesCount: number;
  openPListLinesCount: number;
  openPOrderLinesCount: number;
  warnings: string[];
  blockers: string[];
  binRows: ItemTypeChangePreviewBinRow[];
}

export interface ItemTypeChangeManualBatch {
  batchNo: string;
  quantity: number;
  expiryDate?: string | null;
}

export interface ItemTypeChangeManualEntry {
  itemBinsId: number;
  serialNumbers?: string[];
  batches?: ItemTypeChangeManualBatch[];
}

export interface ItemTypeChangeRequest {
  itemNo: string;
  newItemType: number;
  strategy: ItemTypeChangeStrategy;
  reason?: string;
  manualEntries?: ItemTypeChangeManualEntry[];
}

export interface ItemTypeChangeResult {
  success: boolean;
  itemNo: string;
  oldItemType: number;
  newItemType: number;
  strategyUsed: ItemTypeChangeStrategy;
  rowsDeleted: number;
  rowsInserted: number;
  rowsUpdated: number;
  message?: string;
  warnings: string[];
}

// ============================================================================
// Placeholder-editor types
// ============================================================================

export interface PlaceholderBinRow {
  itemBinsId: number;
  warehouseID: string;
  binCode: string;
  quantity: number;
  serialNo?: string | null;
  batchNo?: string | null;
  expiryDate?: string | null;
  isPlaceholder: boolean;
}

export interface PlaceholderEditorState {
  itemNo: string;
  description?: string | null;
  itemType: number;
  rows: PlaceholderBinRow[];
  placeholderCount: number;
}

export enum PlaceholderEditActionType {
  Update = 0,
  Delete = 1,
  Split = 2,
}

export interface PlaceholderSplitEntry {
  serialNo?: string | null;
  batchNo?: string | null;
  quantity: number;
  expiryDate?: string | null;
}

export interface PlaceholderEditAction {
  type: PlaceholderEditActionType;
  itemBinsId: number;
  serialNo?: string | null;
  batchNo?: string | null;
  expiryDate?: string | null;
  splits?: PlaceholderSplitEntry[];
}

export interface PlaceholderEditRequest {
  itemNo: string;
  actions: PlaceholderEditAction[];
}

export interface PlaceholderEditResult {
  success: boolean;
  itemNo: string;
  rowsUpdated: number;
  rowsDeleted: number;
  rowsInserted: number;
  message?: string;
}
